import { Controller } from "@hotwired/stimulus";

// Lazy widget placeholders (Widgets::ShowView#render_placeholder).
// All placeholders share one queue: at most MAX_IN_FLIGHT requests run at once.
// Placeholders near the viewport go first, the rest follow top-to-bottom.
// Fragments may contain further placeholders; they register on connect.

const MAX_IN_FLIGHT = 3;
const pending = new Set();
const nearViewport = new Set();
let inFlight = 0;

// One screen of lookahead so content is usually ready before it scrolls in.
const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      // Stimulus controller
      const controller = entry.target.widgetFragmentController;
      if (!controller) return;
      if (entry.isIntersecting) {
        nearViewport.add(controller);
      } else {
        nearViewport.delete(controller);
      }
    });
    loadNextWidgetFragment();
  },
  { rootMargin: "100% 0px" },
);

// Document-relative top, stable while scrolling.
function top(controller) {
  return controller.element.getBoundingClientRect().top + window.scrollY;
}

// Near-viewport first, then closest to the top of the page.
function pickNextWidgetFragment() {
  const pool = [...pending].filter((c) => nearViewport.has(c));
  const candidates = pool.length ? pool : [...pending];
  return candidates.reduce(
    (best, c) => (!best || top(c) < top(best) ? c : best),
    null,
  );
}

function loadNextWidgetFragment() {
  while (inFlight < MAX_IN_FLIGHT && pending.size) {
    const controller = pickNextWidgetFragment();
    pending.delete(controller);
    nearViewport.delete(controller);
    observer.unobserve(controller.element);
    inFlight++;
    controller.load().finally(() => {
      inFlight--;
      loadNextWidgetFragment();
    });
  }
}

// Defer so all placeholders connected in the same tick are sorted together.
let scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    loadNextWidgetFragment();
  });
}

export default class extends Controller {
  static values = { src: String };

  connect() {
    this.element.widgetFragmentController = this;
    pending.add(this);
    observer.observe(this.element);
    schedule();
  }

  disconnect() {
    pending.delete(this);
    nearViewport.delete(this);
    observer.unobserve(this.element);
    this.abort?.abort();
  }

  // Swaps the placeholder for the rendered widget. Failures leave the
  // placeholder in place rather than retrying in a loop.
  async load() {
    this.abort = new AbortController();
    try {
      const response = await fetch(this.srcValue, {
        signal: this.abort.signal,
        headers: { Accept: "text/html" },
      });
      if (response.ok) {
        this.element.outerHTML = await response.text();
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("widget fragment failed", this.srcValue, error);
      }
    }
  }
}
